import { AxiosError } from 'axios';
import { PageProps, graphql, navigate } from 'gatsby';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import * as React from 'react';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import { handleSortChange } from 'src/utils/helpers';
import Layout from '../components/Layout';
import Seo from '../components/Seo';
import SearchAdditionalBar from '../components/search_listing/SearchAdditionalBar';
import SearchListing from '../components/search_listing/SearchListing';
import SearchTabs from '../components/search_listing/SearchTabs';
import StickyFacetContainer from '../components/search_listing/StickyFacetContainer';
import { useSearchDrupal } from '../hooks/useSearchDrupal';
import { getHelmetPropsFromMetatagProps, getJsonFromMetatagProps } from '../utils/metatagHelpers';

interface SearchNodeProps extends NodeContent {
  api_slug: string;
}

interface DataProps {
  node: SearchNodeProps;
  translations: Translations;
}

type SortOption = {
  label: string;
  value: string;
};

type SortOptions = {
  [key: string]: SortOption[];
};

const SearchTemplate: React.FC<PageProps<DataProps>> = function ({
  data: { node, translations },
  location,
}) {
  const { t } = useTranslation();
  const helmetProps = getHelmetPropsFromMetatagProps(node.metatag);
  const schemaMarkup = getJsonFromMetatagProps(node.metatag);

  // values for sort options. Should this come from CMS instead?
  const sortOptions: SortOptions = {
    centresquare: [
      {
        label: t('Relevance'),
        value: 'relevance',
      },
      {
        label: t('Latest'),
        value: 'latest',
      },
      {
        label: t('Read/Watch Time'),
        value: 'read_watch_time',
      },
      {
        label: t('Alphabetical'),
        value: 'title',
      },
    ],
    events: [
      // translations for these terms don't exist. Events are only in English
      {
        label: t('Newest to Oldest'),
        value: 'latest',
      },
      {
        label: t('Relevance'),
        value: 'relevance',
      },
      {
        label: t('PI Event Duration'),
        value: 'duration',
      },
      {
        label: t('Title'),
        value: 'title',
      },
    ],
  };

  const params = new URLSearchParams(location.search);
  const sortBy = params.get('sort_by');

  const getComponentSearchResult = React.useCallback(() => {
    let componentToRender = null;
    switch (node.api_slug) {
      case 'science-of-nutrition':
        componentToRender = <SearchListing.ContentSearchResults />;
        break;
      case 'microbiome-forum':
        componentToRender = <SearchListing.ContentSearchResults />;
        break;
      case 'centresquare':
        componentToRender = <SearchListing.ResourceSearchResults />;
        break;
      case 'events':
        componentToRender = <SearchListing.EventSearchResults />;
        break;
      case 'about':
        componentToRender = <SearchListing.ContentSearchResults />;
        break;
      default:
        break;
    }
    return componentToRender;
  }, [node.api_slug]);

  const contextualFilters = React.useMemo(
    () => ({
      language: node.langcode,
    }),
    [node.langcode],
  );

  function onChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const { url } = event.currentTarget.dataset;
    if (url) {
      const facetUrlObject = new URL(url);
      const facetSearchParams = new URLSearchParams(facetUrlObject.search);
      Object.keys(contextualFilters).forEach(key => facetSearchParams.delete(key));
      if (facetSearchParams.toString() !== new URLSearchParams(location.search).toString()) {
        navigate(`?${facetSearchParams}`);
      }
    }
  }

  const sp = React.useMemo(() => {
    const queryParams = Object.fromEntries(new URLSearchParams(location.search)) || {};
    return { ...queryParams, ...contextualFilters, api_slug: node.api_slug };
  }, [location.search, contextualFilters, node.api_slug]);

  const { data: searchData, error, isFetching } = useSearchDrupal(sp, node.langcode);

  return (
    <Layout
      node={node}
      metaData={node.metatag}
      language={node.langcode}
      translations={translations}
    >
      <Seo {...helmetProps} schemaMarkup={schemaMarkup} />
      <SearchAdditionalBar />
      <SearchTabs language={node.langcode}>
        <Container className="py-3">
          <SearchListing
            data={searchData}
            error={(error as AxiosError) || t('Sorry, there was an error')}
            loaded={!isFetching}
          >
            <>
              <Row className="align-items-center py-3">
                <SearchListing.SearchHeader
                  title={`${t('Search Results')}`}
                  hideFilters={node.api_slug !== 'centresquare' && node.api_slug !== 'events'}
                  onFacetRemove={onChange}
                />
              </Row>
              <Row className="position-relative">
                <Col lg={3} className="py-3 d-none d-lg-block">
                  <StickyFacetContainer>
                    {sortOptions[node.api_slug] && (
                      <SearchListing.Sorting
                        options={sortOptions[node.api_slug]}
                        initialValue={sortBy || sortOptions[node.api_slug][0].value}
                        onChange={e => handleSortChange(e, location, navigate)}
                      />
                    )}
                    <SearchListing.SearchFacets
                      onChange={onChange}
                      sortBy={
                        sortBy || sortOptions[node.api_slug]
                          ? sortOptions[node.api_slug][0].value
                          : ''
                      }
                    />
                  </StickyFacetContainer>
                </Col>
                <Col>
                  {getComponentSearchResult()}
                  <SearchListing.Pagination />
                </Col>
                <SearchListing.FacetModal onChange={onChange} />
              </Row>
            </>
          </SearchListing>
        </Container>
      </SearchTabs>
    </Layout>
  );
};

export default SearchTemplate;

export const query = graphql`
  query ($id: String!, $language: String!, $nid: Int!) {
    node: nodeSearchPage(id: { eq: $id }, langcode: { eq: $language }) {
      internal {
        type
      }
      title
      path {
        alias
      }
      default_langcode
      langcode
      ...Metatag
      api_slug: field_api_slug
    }
    translations: allNodeSearchPage(
      filter: { drupal_internal__nid: { eq: $nid }, langcode: { ne: $language } }
    ) {
      edges {
        node {
          langcode
          path {
            alias
          }
        }
      }
    }
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;
